import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";

/**
 * AutoSaveIndicator — a tiny, almost-invisible "Saved automatically" label.
 *
 * Pass the value you want to watch (e.g. textarea content) via `watch`.
 * Each time the watched value changes, the indicator briefly fades in
 * with "Saving…" and then settles into "Saved automatically" for a few
 * seconds before fading back out. Idle state is empty so it never
 * crowds the writing surface.
 *
 * The component does not save anything itself — saving is the parent's
 * responsibility. This is purely the UI surface for an already-saving system.
 */
export default function AutoSaveIndicator({ watch, debounceMs = 600, label = "Saved automatically" }) {
  const [phase, setPhase] = useState("idle"); // idle | saving | saved

  useEffect(() => {
    if (watch === undefined || watch === null) return;
    setPhase("saving");
    const savingTimer = setTimeout(() => setPhase("saved"), debounceMs);
    const idleTimer = setTimeout(() => setPhase("idle"), debounceMs + 2400);
    return () => {
      clearTimeout(savingTimer);
      clearTimeout(idleTimer);
    };
    // We only want this to react to the watched value changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  return (
    <div
      aria-live="polite"
      className="inline-flex items-center gap-1.5 select-none"
      style={{
        minHeight: 18,
        color: "var(--un-text-3)",
        fontFamily: "Manrope, sans-serif",
        fontSize: 11,
        letterSpacing: "0.02em",
      }}
      data-testid="autosave-indicator"
    >
      <AnimatePresence mode="wait">
        {phase === "saving" && (
          <motion.span
            key="saving"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.7 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            Saving…
          </motion.span>
        )}
        {phase === "saved" && (
          <motion.span
            key="saved"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.6 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-1"
          >
            <Check size={10} aria-hidden style={{ color: "var(--un-plum)" }} />
            {label}
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  );
}
